<template>
  <div @click.stop="">
    <audio :id="idPrefix" :src="file.path" />
    <div v-if="!minimal" class="audio-card">
      <div class="audio-card__header">
        <div class="audio-card__copy">
          <span>Аудиозапись</span>
          <strong>{{ file.origin_filename }}</strong>
        </div>
        <button type="button" class="action-button action-delete" @click="onClickDelete">
          <b-icon class="action-icon" icon="trash2" />
          <span>Удалить</span>
        </button>
      </div>
      <audio controls :src="file.path" class="w-100 audio" />
    </div>
    <div v-else class="minimal-player">
      <button
        type="button"
        class="minimal-player__button"
        @click.stop="isPlaying ? onClickPause() : onClickPlay()"
      >
        {{ isPlaying ? 'Пауза' : 'Слушать' }}
      </button>
      <small class="minimal-player__time">
        {{ formattedCurrentTime }} / {{ formattedDuration }}
      </small>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

export default {
  name: 'AudioPlayer',
  props: {
    file: { type: Object, required: true },
    minimal: { type: Boolean, default: false },
  },
  computed: {
    formattedDuration() {
      return dayjs.duration(this.duration * 1000).format('mm:ss');
    },
    formattedCurrentTime() {
      return dayjs.duration(this.currentTime * 1000).format('mm:ss');
    },
  },
  data() {
    return {
      idPrefix: crypto.getRandomValues(new Uint8Array(5)).join(''),
      audioElement: undefined,
      isPlaying: false,
      duration: 0,
      currentTime: 0,
    };
  },
  mounted() {
    this.applyListeners();
  },
  beforeDestroy() {
    this.removeListeners();
  },
  methods: {
    async onClickDelete() {
      const userAgreed = await this.$bvModal.msgBoxConfirm(
        'Вы действительно хотите удалить аудио?',
        {
          okTitle: 'Удалить',
          cancelTitle: 'Отмена',
          cancelVariant: 'outline-secondary',
        },
      );

      if (!userAgreed) {
        return;
      }

      this.$emit('delete', this.file);
    },
    applyListeners() {
      this.audioElement = document.getElementById(this.idPrefix);
      this.audioElement?.addEventListener('playing', this.handlePlayingEvent);
      this.audioElement?.addEventListener('loadedmetadata', this.handleLoadedMetadata);
      this.audioElement?.addEventListener('timeupdate', this.handleTimeUpdate);
    },
    removeListeners() {
      this.audioElement?.removeEventListener('playing', this.handlePlayingEvent);
      this.audioElement?.removeEventListener('loadedmetadata', this.handleLoadedMetadata);
      this.audioElement?.removeEventListener('timeupdate', this.handleTimeUpdate);
    },
    handlePlayingEvent() {
      this.isPlaying = true;
    },
    handleLoadedMetadata(event) {
      this.duration = event.target.duration;
    },
    handleTimeUpdate(event) {
      this.currentTime = event.target.currentTime;
    },
    onClickPlay() {
      this.audioElement?.play();
    },
    onClickPause() {
      this.audioElement?.pause();
      this.isPlaying = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.audio {
  display: block;
  height: 42px;

  &:focus-visible {
    outline: none;
  }
}

.audio-card {
  padding: 14px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #f9fafb;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 12px;
  }

  &__copy {
    display: flex;
    min-width: 0;
    flex-direction: column;

    span {
      margin-bottom: 2px;
      color: #98a2b3;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    strong {
      overflow: hidden;
      color: #344054;
      font-size: 14px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.minimal-player {
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;

  &__button {
    padding: 5px 10px;
    color: #475467;
    border: 1px solid #d0d5dd;
    border-radius: 7px;
    background: #fff;
    font-size: 12px;
    font-weight: 600;
    transition: color 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;

    &:hover {
      color: #c80c0b;
      border-color: rgba(200, 12, 11, 0.35);
      background: #fff7f7;
    }
  }

  &__time {
    color: #667085;
    font-variant-numeric: tabular-nums;
  }
}
</style>
